import React, { useEffect, useMemo, useState } from 'react';
import {
  Link, useLocation, useNavigate, useParams, useSearchParams,
} from 'react-router-dom';
import {
  Activity, ArrowLeft, BellRing, Flame, LifeBuoy, Mail, Phone, TrendingUp,
} from 'lucide-react';
import { getStudent360, isUuid, STUDENT_DETAIL_INCLUDES } from '../../api/students360';
import { getTeams } from '../../api/teams';
import { unwrapList } from '../../utils/api';
import InlineEmpty from '../../components/InlineEmpty/InlineEmpty';
import MetricCard from '../../components/MetricCard/MetricCard';
import PageEmptyState from '../../components/PageEmptyState/PageEmptyState';
import StudentAchievements from '../../components/StudentAchievements/StudentAchievements';
import SubTabs from '../../components/SubTabs/SubTabs';
import ContactLinks, { mailHref, telHref } from '../../components/Student360/ContactLinks';
import CourseCard from '../../components/Student360/CourseCard';
import OrdersSection from '../../components/Student360/OrdersSection';
import PracticeSection from '../../components/Student360/PracticeSection';
import { presenceLabel } from '../../components/Student360/PresenceBadge';
import ResultsSection from '../../components/Student360/ResultsSection';
import StudentAvatar from '../../components/Student360/StudentAvatar';
import useDocumentTitle from '../../components/Student360/useDocumentTitle';
import { AccountStatusBadge, AtRiskChip } from '../../components/Student360/StatusChips';
import TicketsSection from '../../components/Student360/TicketsSection';
import {
  STUDENT_TIME_ZONE, agoText, nowFrom, presenceView,
} from '../../components/Student360/time';
import { usePermissions } from '../../hooks/usePermissions';
import { useI18n } from '../../i18n/I18nContext';
import { formatDate, formatNumber } from '../../utils/datetime';
import { groupTone } from '../Students/groupTone';
import useIsPhone from '../Students/useIsPhone';
import SendReminderModal from './SendReminderModal';
import StudentDetailSkeleton from './StudentDetailSkeleton';
import StudentReminders from './StudentReminders';
import './StudentDetail.css';

/**
 * The student page: GET /crm/students/:id with every facet asked for; the server leaves out the ones
 * the signed-in account may not see and lists what it granted in `meta.groups`. So there is no role
 * logic here: a section is drawn when its key is in the response, a contact line when `phone` or
 * `email` is on the identity, a lesson line when the enrollment carries `currentLesson`.
 *
 * The page is a hero (who, how to reach them, when they were here), up to four tiles (progress,
 * last seen, streak, open tickets), then the work in one column with SubTabs (Courses · Results ·
 * Practice, `?tab=`) beside a rail with the tickets and orders, so a long student is two short
 * screens instead of one very long one.
 *
 * /students/:userId opens every course; /students/:userId?courseId= (from a course roster or a
 * course-filtered Directory) opens that one course, with a link back to all of them.
 */

const COURSES_ON_PHONE = 2;

function StudentDetail() {
  const { t, lang } = useI18n();
  const { userId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const courseParam = searchParams.get('courseId') || '';
  const courseId = isUuid(courseParam) ? courseParam : '';

  const [state, setState] = useState({ phase: 'loading', data: null, error: null });
  const [retryToken, setRetryToken] = useState(0);
  const [teams, setTeams] = useState([]);

  useEffect(() => {
    if (!isUuid(userId)) {
      setState({ phase: 'error', data: null, error: { code: 404 } });
      return undefined;
    }
    let cancelled = false;
    setState({ phase: 'loading', data: null, error: null });
    getStudent360(userId, { include: STUDENT_DETAIL_INCLUDES, courseId })
      .then((data) => { if (!cancelled) setState({ phase: 'ready', data, error: null }); })
      .catch((error) => { if (!cancelled) setState({ phase: 'error', data: null, error }); });
    return () => { cancelled = true; };
  }, [userId, courseId, retryToken]);

  // The CRM teams the student belongs to; a nicety, so a failure just leaves the chips out.
  useEffect(() => {
    let cancelled = false;
    getTeams().then((r) => { if (!cancelled) setTeams(unwrapList(r)); }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const groups = useMemo(() => {
    const out = [];
    teams.forEach((team) => (team.members || []).forEach((member) => {
      if (member.userId === userId) out.push({ id: team.id, name: team.name, type: team.type });
    }));
    return out;
  }, [teams, userId]);

  const goBack = () => {
    // A page opened from a link has nothing to go back to: land on the students hub instead.
    if (location.key === 'default') navigate('/students');
    else navigate(-1);
  };

  const { phase, data, error } = state;
  let body = null;
  if (phase === 'loading') {
    body = <StudentDetailSkeleton label={t('student360.detail.loading')} />;
  } else if (phase === 'error') {
    const code = error?.code;
    body = code === 404 ? (
      <PageEmptyState
        variant="students"
        title={t('student360.detail.notFoundTitle')}
        description={t('student360.detail.notFoundDesc')}
        actionLabel={t('student360.detail.toDirectory')}
        onAction={() => navigate('/students')}
      />
    ) : (
      <PageEmptyState
        variant={code === 403 ? 'forbidden' : 'error'}
        title={code === 403 ? t('student360.detail.noAccessTitle') : t('student360.errors.title')}
        description={code === 403 ? t('student360.errors.noAccessDesc') : t('student360.errors.desc')}
        actionLabel={code === 403 ? undefined : t('student360.errors.retry')}
        onAction={code === 403 ? undefined : () => setRetryToken((n) => n + 1)}
      />
    );
  } else if (data) {
    body = (
      <StudentBody
        data={data}
        userId={userId}
        courseId={courseId}
        groups={groups}
        lang={lang}
      />
    );
  }

  const name = data?.identity?.name;
  useDocumentTitle(name || t('student360.detail.title'));

  return (
    <div className="sd-page">
      <div className="page-head page-head--back">
        <button
          type="button"
          className="ds-btn ds-btn-secondary ds-btn-icon page-back"
          onClick={goBack}
          aria-label={t('student360.detail.back')}
          title={t('student360.detail.back')}
        >
          <ArrowLeft aria-hidden="true" />
        </button>
        <div className="page-head-text">
          <p className="page-eyebrow">{t('student360.hub.title')}</p>
          <h1 className="page-title">{name || t('student360.detail.title')}</h1>
        </div>
      </div>
      {body}
    </div>
  );
}

/** The four tiles under the hero; a tile is drawn only when the facet behind it was granted. */
function buildTiles({
  data, enrollments, now, t,
}) {
  const tiles = [];
  const { presence, practice, tickets } = data;

  if (enrollments.length > 0) {
    const average = Math.round(
      enrollments.reduce((sum, item) => sum + (Number(item.progressPercent) || 0), 0) / enrollments.length,
    );
    tiles.push({
      key: 'progress',
      title: t('student360.detail.kpi.progress'),
      value: `${average}%`,
      change: t('student360.detail.kpi.courses', { count: enrollments.length }),
      icon: <TrendingUp />,
      tone: 'brand',
    });
  }

  const view = presence ? presenceView(presence, now) : null;
  if (view) {
    const { text } = presenceLabel(t, view);
    const tone = { online: 'success', today: 'success', away: 'warning', cold: 'danger' }[view.tone] || 'neutral';
    tiles.push({
      key: 'seen',
      title: t('student360.presence.lastSeen'),
      value: text,
      change: view.loginExact
        ? t('student360.presence.loginAgo', { when: agoText(t, view.login) })
        : t('student360.presence.loginAgo', { when: '—' }),
      icon: <Activity />,
      tone,
    });
  }

  if (practice) {
    tiles.push({
      key: 'streak',
      title: t('student360.practice.streak'),
      value: t('student360.detail.kpi.streakDays', { count: practice.streak.current }),
      change: t('student360.detail.kpi.streakHint', {
        level: practice.xp.level,
        xp: formatNumber(practice.xp.total, { decimals: 0 }),
      }),
      icon: <Flame />,
      tone: 'coral',
    });
  }

  if (tickets) {
    tiles.push({
      key: 'tickets',
      title: t('student360.detail.kpi.openTickets'),
      value: tickets.open ?? 0,
      change: t('student360.detail.kpi.ticketsHint', { total: tickets.total ?? (tickets.items || []).length }),
      icon: <LifeBuoy />,
      tone: (tickets.open ?? 0) > 0 ? 'warning' : 'success',
    });
  }
  return tiles;
}

function StudentBody({
  data, userId, courseId, groups, lang,
}) {
  const { t } = useI18n();
  const { isAdminOrTeacher, canSeeNav } = usePermissions();
  const phone = useIsPhone();
  const [params, setParams] = useSearchParams();
  const [allCourses, setAllCourses] = useState(false);
  // Reminders: the roles that work with them (the same ones the Reminders page is for) may send one
  // to this student and see what the student was sent. The server decides again on every call.
  const canRemind = canSeeNav?.('reminders') === true;
  const [reminding, setReminding] = useState(false);
  const [remindersKey, setRemindersKey] = useState(0);
  const { identity, presence, meta } = data;
  const granted = new Set(meta?.groups || []);
  const now = nowFrom(meta);
  const enrollments = Array.isArray(data.enrollments) ? data.enrollments : [];
  const learningByCourse = new Map((data.learning?.courses || []).map((course) => [course.courseId, course]));
  const hasContact = 'phone' in identity || 'email' in identity;
  const hasBadges = identity.accountStatus === 'BANNED' || identity.accountStatus === 'DISABLED'
    || data.atRisk === true || groups.length > 0;
  const callHref = identity.phone ? telHref(identity.phone) : null;
  const writeHref = identity.email ? mailHref(identity.email) : null;

  // The work area: one tab per facet the response holds
  const tabs = [
    granted.has('LR') && { key: 'courses', label: t('student360.detail.courses'), count: enrollments.length },
    data.results && { key: 'results', label: t('student360.results.title') },
    data.practice && { key: 'practice', label: t('student360.practice.title') },
  ].filter(Boolean);
  const wanted = params.get('tab');
  const active = (tabs.find((tab) => tab.key === wanted) || tabs[0])?.key;
  const setTab = (key) => {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      if (key === tabs[0].key) next.delete('tab');
      else next.set('tab', key);
      return next;
    }, { replace: true });
  };

  const tiles = buildTiles({
    data, enrollments, now, t,
  });
  const hasRail = Boolean(data.tickets || data.orders || canRemind);
  const shownCourses = phone && !allCourses ? enrollments.slice(0, COURSES_ON_PHONE) : enrollments;

  const panel = (() => {
    if (active === 'courses') {
      return (
        <section className="sd-panel" aria-label={t('student360.detail.courses')}>
          {courseId && (
            <p className="sd-narrow">
              {t('student360.detail.oneCourse')}
              {' '}
              <Link to={`/students/${userId}`}>{t('student360.detail.showAllCourses')}</Link>
            </p>
          )}
          {enrollments.length === 0 ? (
            <InlineEmpty
              kind="students"
              surface="card"
              title={courseId ? t('student360.detail.noCourseMatch') : t('student360.detail.noCourses')}
            />
          ) : (
            <>
              <div className="sd-courses">
                {shownCourses.map((enrollment) => (
                  <CourseCard
                    key={enrollment.courseId}
                    enrollment={enrollment}
                    course={learningByCourse.get(enrollment.courseId)}
                    now={now}
                    focus={Boolean(courseId) && enrollment.courseId === courseId}
                  />
                ))}
              </div>
              {phone && enrollments.length > COURSES_ON_PHONE && (
                <button type="button" className="ds-btn ds-btn-secondary sd-more" onClick={() => setAllCourses((v) => !v)}>
                  {allCourses
                    ? t('student360.practice.showLess')
                    : `${t('student360.detail.allCourses')} (${enrollments.length})`}
                </button>
              )}
            </>
          )}
        </section>
      );
    }
    if (active === 'results') return <ResultsSection results={data.results} />;
    if (active === 'practice') {
      return (
        <>
          <PracticeSection
            practice={data.practice}
            assessmentHref={isAdminOrTeacher() ? `/mentor/students/${userId}` : undefined}
          />
          <StudentAchievements userId={userId} />
        </>
      );
    }
    return null;
  })();

  return (
    <>
      <section className="section-card sd-hero">
        <div className="sd-hero-id">
          <StudentAvatar
            name={identity.name}
            avatar={identity.avatar}
            seed={identity.email || identity.id}
            size="lg"
            online={presence?.onlineNow === true}
          />
          <div className="sd-hero-meta">
            {hasBadges && (
              <div className="sd-hero-badges">
                <AccountStatusBadge status={identity.accountStatus} />
                {data.atRisk === true && <AtRiskChip />}
                {groups.map((group) => (
                  <span key={group.id} className="s360-team" data-tone={groupTone(group.type)}>
                    {group.name}
                  </span>
                ))}
              </div>
            )}
            {hasContact && (
              <div className="sd-hero-contact">
                <ContactLinks phone={identity.phone} email={identity.email} stacked={false} />
              </div>
            )}
            {/* When the student was last here is the second tile under this card; this line is what is left */}
            <dl className="sd-hero-facts">
              <div className="sd-fact">
                <dt>{t('student360.detail.joined')}</dt>
                <dd>{formatDate(identity.joinedAt, { lang, timeZone: STUDENT_TIME_ZONE })}</dd>
              </div>
              {identity.locale && (
                <div className="sd-fact">
                  <dt>{t('student360.detail.language')}</dt>
                  <dd>{t(`student360.detail.locale.${identity.locale}`)}</dd>
                </div>
              )}
            </dl>
          </div>
        </div>

        {(callHref || writeHref || canRemind) && (
          <div className="sd-hero-actions">
            {callHref && (
              <a className="ds-btn ds-btn-secondary" href={callHref}>
                <Phone aria-hidden="true" />
                {t('student360.detail.actions.call')}
              </a>
            )}
            {writeHref && (
              <a className="ds-btn ds-btn-secondary" href={writeHref}>
                <Mail aria-hidden="true" />
                {t('student360.detail.actions.email')}
              </a>
            )}
            {canRemind && (
              <button type="button" className="ds-btn ds-btn-secondary" onClick={() => setReminding(true)}>
                <BellRing aria-hidden="true" />
                {t('reminders.student.action')}
              </button>
            )}
          </div>
        )}

      </section>

      {tiles.length > 0 && (
        <div className="kpi-row sd-kpis">
          {tiles.map(({ key, ...tile }) => (
            <MetricCard key={key} {...tile} countUp={false} />
          ))}
        </div>
      )}

      {(tabs.length > 0 || hasRail) && (
        <div className={`sd-work ${hasRail ? 'page-grid page-grid--rail' : ''}`}>
          <div className="sd-main">
            {tabs.length > 1 && (
              <SubTabs
                className="sd-tabs"
                tabs={tabs}
                active={active}
                onChange={setTab}
                ariaLabel={t('student360.detail.sections')}
              />
            )}
            <div className="sd-panel-wrap" key={active}>{panel}</div>
          </div>
          {hasRail && (
            <aside className="col-rail">
              {data.tickets && <TicketsSection tickets={data.tickets} now={now} />}
              {data.orders && <OrdersSection orders={data.orders} />}
              {canRemind && <StudentReminders userId={identity.id || userId} reloadKey={remindersKey} />}
            </aside>
          )}
        </div>
      )}

      {reminding && (
        <SendReminderModal
          student={{ id: identity.id || userId, name: identity.name }}
          onClose={() => setReminding(false)}
          onSent={() => setRemindersKey((n) => n + 1)}
        />
      )}
    </>
  );
}

export default StudentDetail;
